// Zustand der Anwendung: angemeldeter Benutzer, Bestand, Navigation.
//
// Bewusst ohne Zustandsbibliothek. Der gesamte Bestand ist ein Objekt, das
// jede schreibende Anfrage vollständig zurückliefert — es gibt nichts
// zusammenzuführen, nichts ungültig zu machen. Ein Context mit useState reicht
// dafür, und alles andere wäre Werkzeug ohne Aufgabe.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from "react";
import { api, ApiFehler } from "./api.ts";
import type { Benutzer, Portfolio } from "./typen.ts";

// -------------------------------------------------------------- Navigation

export type Sicht =
  | { seite: "dashboard" }
  | { seite: "objekte" }
  | { seite: "objekt"; objektId: string; reiter?: string }
  | { seite: "einheiten" }
  | { seite: "einheit"; objektId: string; einheitId: string; reiter?: string }
  | { seite: "maengel" }
  | { seite: "inventar" }
  | { seite: "vorlage" }
  | { seite: "sanierung" }
  | { seite: "mieter" }
  | { seite: "vertraege" }
  | { seite: "aufgaben" }
  | { seite: "dokumente" }
  | { seite: "kommunikation" }
  | { seite: "finanzen"; bereich?: string }
  | { seite: "konto" }
  | { seite: "bald"; thema: string };

/** Sicht als Adresse, damit Neuladen und Zurück-Taste funktionieren. */
export function sichtZuHash(s: Sicht): string {
  switch (s.seite) {
    case "objekt": return `#/objekt/${s.objektId}${s.reiter ? `/${s.reiter}` : ""}`;
    case "einheit": return `#/einheit/${s.objektId}/${s.einheitId}${s.reiter ? `/${s.reiter}` : ""}`;
    case "bald": return `#/bald/${s.thema}`;
    case "finanzen": return `#/finanzen${s.bereich ? `/${s.bereich}` : ""}`;
    default: return `#/${s.seite}`;
  }
}

export function hashZuSicht(hash: string): Sicht {
  const teile = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  const [erste, zweite, dritte, vierte] = teile;
  switch (erste) {
    case "objekt": return zweite ? { seite: "objekt", objektId: zweite, reiter: dritte } : { seite: "objekte" };
    case "einheit": return zweite && dritte
      ? { seite: "einheit", objektId: zweite, einheitId: dritte, reiter: vierte }
      : { seite: "einheiten" };
    case "bald": return { seite: "bald", thema: zweite ?? "" };
    case "finanzen": return { seite: "finanzen", bereich: zweite };
    case "objekte": case "einheiten": case "maengel": case "inventar":
    case "vorlage": case "sanierung": case "mieter": case "vertraege":
    case "aufgaben": case "dokumente": case "kommunikation": case "konto":
      return { seite: erste };
    default: return { seite: "dashboard" };
  }
}

// ----------------------------------------------------------------- Context

interface ZustandWert {
  benutzer: Benutzer | null;
  /** Kein Netz. Die App zeigt dann bewusst keine alten Daten, sondern sagt es. */
  offline: boolean;
  bestand: Portfolio | null;
  laedt: boolean;
  /** Der Bestand ließ sich nicht laden. Ohne diesen Zustand bliebe die
   *  Anwendung stumm auf „wird geladen“ stehen, ohne Ausweg. */
  bestandFehler: string | null;
  meldung: { text: string; fehler: boolean } | null;
  sicht: Sicht;
  gehe: (s: Sicht) => void;
  anmelden: (email: string, passwort: string) => Promise<void>;
  abmelden: () => Promise<void>;
  /**
   * Führt eine schreibende Aktion aus und übernimmt den zurückgelieferten
   * Bestand. Fehler landen im Streifen statt in der Konsole — der Nutzer soll
   * merken, wenn etwas nicht gespeichert wurde.
   */
  aendern: (aktion: () => Promise<Portfolio>, erfolgstext?: string) => Promise<boolean>;
  neuLaden: () => Promise<void>;
  zeigeMeldung: (text: string, fehler?: boolean) => void;
}

const Zustand = createContext<ZustandWert | null>(null);

export function ZustandGeber({ children }: { children: ReactNode }) {
  const [benutzer, setzeBenutzer] = useState<Benutzer | null>(null);
  const [bestand, setzeBestand] = useState<Portfolio | null>(null);
  const [laedt, setzeLaedt] = useState(true);
  const [meldung, setzeMeldung] = useState<{ text: string; fehler: boolean } | null>(null);
  const [bestandFehler, setzeBestandFehler] = useState<string | null>(null);
  const [sicht, setzeSicht] = useState<Sicht>(() => hashZuSicht(location.hash));
  const [offline, setzeOffline] = useState(() => !navigator.onLine);

  useEffect(() => {
    const an = () => setzeOffline(false);
    const aus = () => setzeOffline(true);
    window.addEventListener("online", an);
    window.addEventListener("offline", aus);
    return () => { window.removeEventListener("online", an); window.removeEventListener("offline", aus); };
  }, []);

  const zeigeMeldung = useCallback((text: string, fehler = false) => {
    setzeMeldung({ text, fehler });
    window.setTimeout(() => setzeMeldung(null), fehler ? 5000 : 2200);
  }, []);

  const gehe = useCallback((s: Sicht) => {
    const hash = sichtZuHash(s);
    if (location.hash !== hash) location.hash = hash;
    setzeSicht(s);
    window.scrollTo(0, 0);
  }, []);

  // Zurück-Taste und von Hand geänderte Adresse berücksichtigen.
  useEffect(() => {
    const beiHash = () => setzeSicht(hashZuSicht(location.hash));
    window.addEventListener("hashchange", beiHash);
    return () => window.removeEventListener("hashchange", beiHash);
  }, []);

  const ladeBestand = useCallback(async () => {
    try {
      setzeBestand(await api.portfolio());
      setzeBestandFehler(null);
    } catch (fehler) {
      // Nicht schlucken: Ohne sichtbaren Fehler steht die Anwendung
      // dauerhaft auf „wird geladen“ und niemand weiß, woran es liegt.
      setzeBestandFehler(fehler instanceof Error ? fehler.message : "Der Bestand ließ sich nicht laden.");
      throw fehler;
    }
  }, []);

  // Beim Start prüfen, ob noch eine Sitzung besteht.
  useEffect(() => {
    void (async () => {
      try {
        const { benutzer: b } = await api.ich();
        setzeBenutzer(b);
        await ladeBestand();
      } catch (fehler) {
        // 401 heißt nur: keine Sitzung. Das ist kein Fehler, sondern der
        // Normalfall beim ersten Aufruf.
        if (fehler instanceof ApiFehler && fehler.status === 401) setzeBestandFehler(null);
        else zeigeMeldung("Der Server ist nicht erreichbar.", true);
      } finally {
        setzeLaedt(false);
      }
    })();
  }, [ladeBestand, zeigeMeldung]);

  const anmelden = useCallback(async (email: string, passwort: string) => {
    const { benutzer: b } = await api.anmelden(email, passwort);
    // Erst laden, dann anmelden: Scheitert der Bestand, bleibt der
    // Anmeldebildschirm mit einer Meldung stehen, statt in eine Anwendung
    // ohne Daten zu führen.
    await ladeBestand();
    setzeBenutzer(b);
  }, [ladeBestand]);

  const abmelden = useCallback(async () => {
    await api.abmelden();
    setzeBenutzer(null);
    setzeBestand(null);
    gehe({ seite: "dashboard" });
  }, [gehe]);

  const aendern = useCallback(async (aktion: () => Promise<Portfolio>, erfolgstext?: string) => {
    try {
      setzeBestand(await aktion());
      if (erfolgstext) zeigeMeldung(erfolgstext);
      return true;
    } catch (fehler) {
      if (fehler instanceof ApiFehler && fehler.status === 401) {
        // Sitzung abgelaufen: zurück zur Anmeldung, ohne Datenverlust zu behaupten.
        setzeBenutzer(null);
        zeigeMeldung("Die Sitzung ist abgelaufen. Bitte erneut anmelden.", true);
      } else {
        zeigeMeldung(fehler instanceof Error ? fehler.message : "Unbekannter Fehler.", true);
      }
      return false;
    }
  }, [zeigeMeldung]);

  const wert = useMemo<ZustandWert>(() => ({
    benutzer, bestand, laedt, bestandFehler, meldung, sicht, offline, gehe,
    anmelden, abmelden, aendern, neuLaden: ladeBestand, zeigeMeldung,
  }), [benutzer, bestand, laedt, bestandFehler, meldung, sicht, offline, gehe,
       anmelden, abmelden, aendern, ladeBestand, zeigeMeldung]);

  return <Zustand.Provider value={wert}>{children}</Zustand.Provider>;
}

export function useZustand(): ZustandWert {
  const wert = useContext(Zustand);
  if (!wert) throw new Error("useZustand außerhalb von ZustandGeber verwendet.");
  return wert;
}

/** Bestand, von dem feststeht, dass er geladen ist — spart Prüfungen in jeder Seite. */
export function useBestand(): Portfolio {
  const { bestand } = useZustand();
  if (!bestand) throw new Error("Bestand noch nicht geladen.");
  return bestand;
}
